import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { isNil, omit } from 'ramda'
import { gql, useLazyQuery } from '@apollo/client'
import { vars } from '@machobear/design-system/css'
import debounce from 'lodash/debounce'
import toInteger from 'lodash/toInteger'
import Range from 'rc-slider'

import { breakpoints, colors } from '@/styles'
import formatCurrency from '@/utils/format-currency'
import useDebounce from '@/hooks/use-debounce'
import CurrencyFormatter from '@/components/currency-formatter'
import { Label } from '@/components/text'

import { handleDataLayerEvent } from './utils'

import 'rc-slider/assets/index.css'

const PRICE_DATA = gql`
  query ($input: PriceGraphInput!) {
    priceGraph(input: $input) {
      price
      count
    }
  }
`

const MAX_PRICE = 10000
const RANGE = { min: 0, max: MAX_PRICE }
const MIN_HEIGHT = 20

const GraphContainer = styled.div`
  padding: 7px 0 0;
  height: 63px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  position: relative;
  width: 100%;
`

const GraphBar = styled.div`
  position: absolute;
  background: ${colors.grey72};
  opacity: ${props => (props.selected ? 1 : 0.3)};
  ${props => `
    height: ${props.$value || 0}%;
    width: calc(${props.$width || 0}% - ${props.condensed ? 0.5 : 1}px);
    left: calc(${props.$offset} * 100% + ${(props.condensed ? 1 : 2) / 2}px);
  `};

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    ${props => `
      width: calc(${props.$width || 0}% - 0.5px);
      left: calc(${props.$offset} * 100% + 1 / 2}px);
    `}
  }
`

const Hidden = styled.span`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`

const Slider = styled.div`
  padding: 0 10px 0;
  .rc-slider-track {
    background-color: ${colors.blue16} !important;
  }
  .rc-slider {
    content: '';
    padding: 0;
    height: 5px;
  }
`

const Labels = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 14px -10px 0;
  color: ${vars.foregroundColor.neutral};
`

const handleStyle = {
  width: '20px',
  height: '20px',
  marginTop: '-8px',
  border: `2px solid ${colors.blue16}`,
  borderRadius: '50%',
  backgroundColor: colors.white,
  boxShadow:
    '0px 4px 6px -2px rgba(0, 0, 0, 0.06), 0px 2px 4px -1px rgba(0, 0, 0, 0.04)',
  opacity: 1,
  outline: 'revert'
}

const rightHandleStyle = {
  ...handleStyle
}
const trackStyle = {
  height: '5px',
  borderRadius: '0px',
  backgroundColor: colors.blue16
}

const railStyle = {
  height: '5px',
  borderRadius: '0px',
  backgroundColor: colors.grey40
}

const renderMinPrice = (min, locale) =>
  isNil(min) ? (
    <CurrencyFormatter value={0} locale={locale} />
  ) : (
    <CurrencyFormatter value={parseInt(min)} locale={locale} />
  )

const renderMaxPrice = (max, locale) =>
  isNil(max) || parseInt(max) >= MAX_PRICE ? (
    <Trans i18nKey="filters:b.listing_filter.edit.price_range_max_value.label">
      <CurrencyFormatter value={MAX_PRICE} locale={locale} />+
    </Trans>
  ) : (
    <CurrencyFormatter value={parseInt(max)} locale={locale} />
  )

const formatMaxPrice = max =>
  isNil(max) || parseInt(max) >= MAX_PRICE ? MAX_PRICE : parseInt(max)

const getBarGraphValues = data => {
  const graph = data?.priceGraph || {}
  const original = Object.values(graph)
  const values = original.reduce((acc, next, i) => {
    if (original.length - 1 <= i) {
      const last = acc[acc.length - 1]
      acc[acc.length - 1] = {
        ...last,
        count: last.count + next.count
      }
      return acc
    }
    acc.push(next)
    return acc
  }, [])
  const [pairs] = original.reduce((acc, next) => {
    if (acc.length <= 0) return [[], next.price]
    const [arr, price] = acc
    arr.push([price, next.price])
    return [arr, next.price]
  }, [])
  const maxBarValue = Math.max(...values.map(({ count }) => count))
  return { values, pairs, maxBarValue }
}

const Bar = ({
  range,
  count,
  height,
  offset,
  width,
  condensed,
  price,
  filter_min,
  filter_max
}) => {
  const { t } = useTranslation()

  const [minPrice, maxPrice] = range
  const min = formatCurrency(minPrice, 0)
  const max = formatCurrency(maxPrice, 0)
  const msg =
    maxPrice < RANGE.max
      ? t('filters:b.listing_filter.edit.price_graph_range.text', {
          defaultValue: '{{count}} listing for between {{min}}-{{max}}',
          defaultValue_plural: '{{count}} listings for between {{min}}-{{max}}',
          count,
          min,
          max
        })
      : t('filters:b.listing_filter.edit.price_graph_max.text', {
          defaultValue: '{{count}} listing for {{min}}+',
          defaultValue_plural: '{{count}} listings for {{min}}+',
          count,
          min
        })
  if (count <= 0) return null
  const isSelected = price >= filter_min && price < filter_max

  return (
    <GraphBar
      title={msg}
      $value={height}
      $offset={offset}
      $width={width}
      condensed={condensed}
      selected={isSelected}
    >
      <Hidden>{msg}</Hidden>
    </GraphBar>
  )
}

const PriceSlider = ({
  min_price = 0,
  max_price = null,
  initial,
  filters,
  onChange,
  boundaries,
  condensed,
  companyId,
  extCompanyId
}) => {
  const router = useRouter()
  const locale = router?.locale
  const initMin = initial.min_price || 0
  const initMax = initial.max_price || MAX_PRICE
  const currMin = min_price || 0
  const currMax = max_price === null ? MAX_PRICE : max_price
  const [cachedPrices, setCachedPrices] = useState()

  const debouncedPrice = useDebounce(
    { min_price, max_price: max_price || RANGE.max },
    300
  )
  const [loadPriceGraph, { previousData, data }] = useLazyQuery(PRICE_DATA)
  const delayedQuery = useMemo(
    () => debounce(loadPriceGraph, 200),
    [loadPriceGraph]
  )

  const { values, pairs, maxBarValue } = getBarGraphValues(
    data ?? previousData ?? cachedPrices
  )
  const getHeight = value =>
    maxBarValue ? (value / maxBarValue) * (100 - MIN_HEIGHT) + MIN_HEIGHT : 0
  const getOffset = value => (value ? value / (RANGE.max - RANGE.min) : 0)
  const getWidth = ([min, max]) => ((max - min) / (RANGE.max - RANGE.min)) * 100

  const [prices, setPrice] = useState({
    min_price: initMin,
    max_price: initMax
  })

  useEffect(() => {
    setCachedPrices(el => (typeof data !== 'undefined' ? data : el))
  }, [data])

  useEffect(() => {
    setPrice({ min_price: currMin, max_price: currMax })
  }, [currMin, currMax])

  useEffect(() => {
    const parsedCompanyId = toInteger(companyId) || undefined
    delayedQuery({
      variables: {
        input: {
          fixed_range: true,
          ...omit(
            [
              'featured',
              'page',
              'page_size',
              'sort',
              'housing_type',
              'min_price',
              'max_price',
              'state_machine',
              parsedCompanyId ? 'cities' : undefined
            ],
            filters
          ),
          company_id: parsedCompanyId,
          sub_company_id: extCompanyId,
          ...boundaries
        }
      }
    })
    return () => {
      delayedQuery.cancel()
    }
  }, [filters, boundaries, delayedQuery, companyId, extCompanyId])

  return (
    <Slider>
      <GraphContainer>
        {values.map(({ count, price }, i) => (
          <Bar
            key={`${price}-${count}`}
            range={pairs[i]}
            count={count}
            height={getHeight(count)}
            offset={getOffset(price)}
            width={getWidth(pairs[i])}
            condensed={condensed}
            price={price}
            filter_min={debouncedPrice.min_price}
            filter_max={debouncedPrice.max_price}
          />
        ))}
      </GraphContainer>
      <Range
        min={RANGE.min}
        max={RANGE.max}
        handleStyle={[handleStyle, rightHandleStyle]}
        trackStyle={[trackStyle, trackStyle]}
        railStyle={railStyle}
        defaultValue={[
          parseInt(prices.min_price),
          formatMaxPrice(prices.max_price)
        ]}
        value={[prices.min_price, prices.max_price]}
        step={100}
        onBeforeChange={() => handleDataLayerEvent('price')}
        onChange={onChange}
        pushable={100}
        allowCross
        range
      />
      <Labels>
        <Label fontSize="14px" type="small">
          {renderMinPrice(min_price, locale)}
        </Label>
        <Label fontSize="14px" type="small">
          {renderMaxPrice(max_price, locale)}
        </Label>
      </Labels>
    </Slider>
  )
}

export default PriceSlider
